import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const BhriguLake = () => {
  const tourData = {
    name: "Bhrigu Lake Trek",
    duration: "03 Nights 04 Days",
    price: "₹ 4,499/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1754935854/vvsalykumt9kuklieib9.webp",
    altitude: "14,000 ft",
    difficulty: "Easy to Moderate",
    region: "Himachal Pradesh",
    highlights: ["High-altitude lake", "Mountain views", "Alpine meadows"],
    bestTime: "May to October",
    description: "The Bhrigu Lake Trek is a beautiful high-altitude trek that takes you to a sacred lake surrounded by stunning mountain views and alpine meadows. This easy to moderate trek offers breathtaking scenery and is perfect for those looking for a short but rewarding Himalayan adventure."
  };

  return (
    <>
      <Helmet>
        <title>Bhrigu Lake Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Bhrigu Lake Trek in Himachal Pradesh. High-altitude lake trek with stunning mountain views and alpine meadows." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              A beautiful high-altitude lake trek with stunning mountain views
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you to one of the most sacred lakes in the Himalayas, named after 
                    the great sage Bhrigu. The lake is surrounded by pristine alpine meadows and offers 
                    spectacular views of snow-capped peaks. The trek is perfect for both beginners and 
                    experienced trekkers looking for a short but memorable mountain adventure.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>High-altitude sacred lake</li>
                    <li>Stunning mountain views</li>
                    <li>Alpine meadows and landscapes</li>
                    <li>Perfect for beginners</li>
                    <li>Short but rewarding trek</li>
                    <li>Photography opportunities</li>
                    <li>Spiritual significance</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Reach Manali and explore the city</h4>
                    <ul>
                      <li>Manali, located close to the valley's lowest point, is one of the most popular tourist destinations in Himachal Pradesh.</li>
                      <li>The word "Manali" conjures up images of waterfalls, songbirds, woods, orchards, and stunning glacial mountains.</li>
                      <li>Manali is the actual beginning point of a historic trade route that traverses the Rohtang and Baralacha passes.</li>
                      <li>There is a fascinating tradition about Manali that states that Manu, the author of the "Manu Samhita," first walked on earth from the heavenly boat at this location after the great deluge.</li>
                      <li>Explore mall road in the evening and take rest for the night in the hotel after dinner.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: From Manali Gulaba to Rola Kholi</h4>
                    <ul>
                      <li>Our representative will pick you up from the area next to Mall Road in Manali and drive you to Gulaba.</li>
                      <li>Gulaba is around 38 kilometres away from Manali, taking between one and two hours to reach.</li>
                      <li>After arriving in Gulaba, we shall begin our journey to Kothi Thel.</li>
                      <li>Following breakfast, begin your journey to Rola Kholi from Kothi Thel, which is around a five to six hour trek.</li>
                      <li>Rest in Rola Kholi, have dinner and sleep for the night.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: From Rola Kholi to Bhrigu Lake and Back</h4>
                    <ul>
                      <li>Following breakfast, begin your journey to the breathtaking Bhrigu Lake.</li>
                      <li>Today will be a rather lengthy journey, but it will be one of the most breathtaking ones.</li>
                      <li>Experience the stunning alpine meadows and snow-covered landscapes as you ascend to 14,000 feet.</li>
                      <li>Following the completion of some unforgettable experiences at Bhrigu Lake, make your way down to Rola Kholi.</li>
                      <li>Camp at Rola Kholi for the night, surrounded by the beauty of the Himalayas.</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: From Rola Kholi to Manali</h4>
                    <ul>
                      <li>Following breakfast, make your way back down to Gulaba from Rola Kholi.</li>
                      <li>Enjoy the descent through the beautiful landscapes you climbed the previous days.</li>
                      <li>Transfer back to Manali, completing your Bhrigu Lake adventure.</li>
                      <li>Arrive back in Manali with memories of an incredible Himalayan trek experience.</li>
                      <li>Your Bhrigu Lake trek concludes with a sense of achievement and unforgettable mountain memories.</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="things-to-carry-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Things To Carry</h3>
                  <p className="carry-intro">All items listed are essential, unless marked as optional. Items are divided into five categories for your convenience.</p>
                  
                  <div className="carry-categories">
                    <div className="carry-category">
                      <h4><i className="fas fa-hiking"></i> Trekking Gear</h4>
                      <ul>
                        <li>Rucksack with rain cover</li>
                        <li>Day pack bag</li>
                        <li>Head torch with spare batteries</li>
                        <li>UV protection sunglasses</li>
                        <li>Water bottles: Two 1-liter bottles</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-shoe-prints"></i> Footwear</h4>
                      <ul>
                        <li>Non-skid, deep treaded, high-ankle trekking shoes: 1 pair</li>
                        <li>Lightweight slippers or sandals: 1 pair</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-tshirt"></i> Clothing</h4>
                      <ul>
                        <li>Quick-dry warm lower or track pants: 2</li>
                        <li>Full-sleeve T-shirts/sweatshirts: 1 for every 2 days of trekking</li>
                        <li>Thick woolen socks: 1 pair for every 2 days of trekking</li>
                        <li>Thermal body warmer (upper & lower): 1 set</li>
                        <li>Undergarments: 1 for each day of trekking</li>
                        <li>Warm jacket (closed at wrist & neck): 1</li>
                        <li>Full-sleeve sweater: 1</li>
                        <li>Rainwear (jacket & pants): 1 set</li>
                        <li>Waterproof, warm gloves: 1 pair</li>
                        <li>Woolen cap: 1</li>
                        <li>Sun-shielding hat: 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-soap"></i> Toiletries</h4>
                      <ul>
                        <li>Personal toiletries kit: Small towel, toilet paper, paper soap, bar soap, toothbrush, toothpaste, cold cream, etc.</li>
                        <li>Sunscreen lotion: Small pack</li>
                        <li>Lip balm: Small pack</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-utensils"></i> Utensils</h4>
                      <ul>
                        <li>Small, lightweight, leak-proof lunch box: 1</li>
                        <li>Plate: 1</li>
                        <li>Spoon: 1</li>
                        <li>Tea/coffee mug (plastic): 1</li>
                      </ul>
                    </div>

                    <div className="carry-category">
                      <h4><i className="fas fa-plus-circle"></i> Miscellaneous</h4>
                      <ul>
                        <li className="optional">Camera: (Optional)</li>
                        <li>Personal medicines: Bring plenty if needed; consult your doctor before the trek.</li>
                        <li className="optional">Dry fruits, nuts, chocolate bars (Optional)</li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>All meals during the trek (breakfast, lunch, dinner)</li>
                    <li>Accommodation in tents during the trek</li>
                    <li>Professional trek leader and support staff</li>
                    <li>All necessary permits and entry fees</li>
                    <li>First aid kit and emergency support</li>
                    <li>Transportation from Manali to trek start point and back</li>
                    <li>Camping equipment (tents, sleeping bags, mattresses)</li>
                    <li>Kitchen and dining tent with all necessary utensils</li>
                    <li>Porters for carrying common equipment</li>
                    <li>Safety equipment and communication devices</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>Anything that has not been covered in the inclusions mentioned above</li>
                    <li>Porters or mules to carry your luggage. The cost per bag (10/12 kg) is 300 per day</li>
                    <li>Any kind of personal expenses</li>
                    <li>Any kind of personal medication</li>
                    <li>Transportation to and from Manali</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Any additional expenses due to weather conditions or unforeseen circumstances</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>14,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>20 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy to Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person (Ex. Manali)</h4>
                  <ul>
                    <li><strong>Quad Sharing:</strong> ₹ 4,499 + 5% GST</li>
                    <li><strong>Triple Sharing:</strong> ₹ 4,999 + 5% GST</li>
                    <li><strong>Double Sharing:</strong> ₹ 5,499 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Manali:</strong> Mall Road area between 08:00 AM to 09:00 AM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>High-altitude sacred lake</li>
                    <li>Stunning mountain views</li>
                    <li>Alpine meadows and landscapes</li>
                    <li>Perfect for beginners</li>
                    <li>Short but rewarding trek</li>
                    <li>Photography opportunities</li>
                    <li>Spiritual significance</li>
                    <li>Professional trek leader and support staff</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default BhriguLake;
